import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import {
	MagnifyingGlassIcon,
	AdjustmentsHorizontalIcon,
	CalendarIcon,
	TableCellsIcon,
	PlusIcon,
} from '@heroicons/react/24/outline';

import { MainContent, PageHeading } from '../../components/Shell/Content';
import { DataTable, FilterDropdown, TextStack } from '../../components/nextgen';
import { Calendar } from '../../components/Calendar';
import PMScheduleBarChart from '../../components/PMScheduleBarChart';

import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';
import useSettings from '../../hooks/useSettings';

import { getPMSchedules, getPMScheduleCalendar } from '../../api';

// Frequency display
const FREQUENCY_LABELS = {
	daily: 'Daily',
	weekly: 'Weekly',
	biweekly: 'Bi-weekly',
	monthly: 'Monthly',
	quarterly: 'Quarterly',
	biyearly: 'Bi-yearly',
	yearly: 'Yearly',
	custom: 'Custom',
};

// Frequency badge component
const FrequencyBadge = ({ frequency }) => {
	const label = FREQUENCY_LABELS[frequency] || frequency;
	return (
		<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
			{label}
		</span>
	);
};

// Status badge for active/inactive
const StatusBadge = ({ active }) => (
	<span
		className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
			active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
		}`}
	>
		{active ? 'Active' : 'Inactive'}
	</span>
);

// Stat Card
const StatCard = ({ label, value, valueColor = 'text-gray-900' }) => (
	<div className="bg-white border border-gray-200 rounded-lg p-5">
		<p className="text-sm text-gray-500 mb-1">{label}</p>
		<p className={`text-3xl font-semibold ${valueColor}`}>{value}</p>
	</div>
);

// View Toggle
const ViewToggle = ({ view, onViewChange }) => (
	<div className="flex items-center bg-gray-100 rounded-lg p-1">
		<button
			onClick={() => onViewChange('table')}
			className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
				view === 'table'
					? 'bg-white text-gray-900 shadow-sm'
					: 'text-gray-600 hover:text-gray-900'
			}`}
		>
			<TableCellsIcon className="w-4 h-4" />
			Table
		</button>
		<button
			onClick={() => onViewChange('calendar')}
			className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
				view === 'calendar'
					? 'bg-white text-gray-900 shadow-sm'
					: 'text-gray-600 hover:text-gray-900'
			}`}
		>
			<CalendarIcon className="w-4 h-4" />
			Monthly Breakdown
		</button>
	</div>
);

const PMSchedules = () => {
	const navigate = useNavigate();
	const { id: scheduleIdFromUrl } = useParams();
	const [searchParams] = useSearchParams();
	const assetIdFromUrl = searchParams.get('assetId');
	const { setOpen: openEditPMSchedule } = useModal('editPMSchedule');
	const { setOpen: openPMScheduleOverview } = useModal('pmScheduleOverview');
	const { timezone } = useSettings();

	// View state
	const [view, setView] = useState('table');
	const [monthSteps, setMonthSteps] = useState(0);

	// Filters
	const [search, setSearch] = useState('');
	const [statusFilter, setStatusFilter] = useState('active');
	const [frequencyFilter, setFrequencyFilter] = useState('all');
	const [categoryFilter, setCategoryFilter] = useState('all');
	const [assetFilter, setAssetFilter] = useState(assetIdFromUrl || null);

	// Mobile infinite scroll
	const MOBILE_PAGE_SIZE = 10;
	const [mobileItemsToShow, setMobileItemsToShow] = useState(MOBILE_PAGE_SIZE);
	const observerRef = useRef(null);

	// Reset mobile pagination when filters change
	useEffect(() => {
		setMobileItemsToShow(MOBILE_PAGE_SIZE);
	}, [search, statusFilter, frequencyFilter, categoryFilter, assetFilter]);

	// Callback ref for infinite scroll
	const loadMoreRef = useCallback((node) => {
		if (observerRef.current) {
			observerRef.current.disconnect();
		}

		if (node) {
			observerRef.current = new IntersectionObserver(
				(entries) => {
					if (entries[0].isIntersecting) {
						setMobileItemsToShow((prev) => prev + MOBILE_PAGE_SIZE);
					}
				},
				{ root: null, rootMargin: '100px', threshold: 0 },
			);
			observerRef.current.observe(node);
		}
	}, []);

	// Data - Table view
	const {
		value: schedulesData,
		execute: refreshSchedules,
		status: loadingStatus,
	} = useAsync(getPMSchedules, {
		defaultPayload: {
			pageSize: null,
			status: statusFilter,
			...(assetFilter && { assetId: assetFilter }),
		},
	});

	// Data - Calendar view
	const { value: calendarData, execute: refreshCalendar } = useAsync(
		getPMScheduleCalendar,
		{
			immediate: false,
		},
	);

	useEffect(() => {
		refreshSchedules({
			pageSize: null,
			status: statusFilter,
			...(assetFilter && { assetId: assetFilter }),
		});
	}, [statusFilter, assetFilter]);

	// Refresh calendar when in calendar view
	useEffect(() => {
		if (view === 'calendar') {
			const startDate = moment()
				.add(monthSteps, 'months')
				.startOf('month')
				.format('YYYY-MM-DD');
			const endDate = moment()
				.add(monthSteps, 'months')
				.endOf('month')
				.format('YYYY-MM-DD');
			refreshCalendar({ startDate, endDate });
		}
	}, [view, monthSteps]);

	// Get unique categories for filter
	const categories = useMemo(() => {
		const cats = schedulesData?.rows
			?.map((s) => s.category)
			.filter(Boolean)
			.reduce((acc, cat) => {
				if (!acc.find((c) => c.id === cat.id)) {
					acc.push(cat);
				}
				return acc;
			}, []);
		return cats || [];
	}, [schedulesData]);

	// Process schedules (search and filter)
	const processedSchedules = useMemo(() => {
		let result = schedulesData?.rows || [];

		// Search
		if (search) {
			const q = search.toLowerCase();
			result = result.filter(
				(s) =>
					s.name?.toLowerCase().includes(q) ||
					s.description?.toLowerCase().includes(q),
			);
		}

		// Frequency filter
		if (frequencyFilter !== 'all') {
			result = result.filter((s) => s.occurrences === frequencyFilter);
		}

		// Category filter
		if (categoryFilter !== 'all') {
			result = result.filter((s) => s.categoryId === categoryFilter);
		}

		return result;
	}, [schedulesData, search, frequencyFilter, categoryFilter]);

	// Transform calendar data to events format
	const calendarEvents = useMemo(() => {
		if (!calendarData) return [];
		return calendarData.map((event) => ({
			id: event.id,
			date: event.date,
			title: event.title,
			type: event.type,
			priority: event.priority,
		}));
	}, [calendarData]);

	const handleRowClick = (schedule) => {
		navigate(`/pm-schedules/${schedule.id}`);
		openPMScheduleOverview({
			scheduleId: schedule.id,
			onClose: () => {
				navigate('/pm-schedules');
			},
			onSuccess: () => {
				refreshSchedules({
					pageSize: null,
					status: statusFilter,
					...(assetFilter && { assetId: assetFilter }),
				});
			},
		});
	};

	const handleCreate = () => {
		openEditPMSchedule({
			onSuccess: () =>
				refreshSchedules({
					pageSize: null,
					status: statusFilter,
					...(assetFilter && { assetId: assetFilter }),
				}),
		});
	};

	// Open schedule from URL if ID is present
	useEffect(() => {
		if (scheduleIdFromUrl && schedulesData?.rows) {
			const schedule = schedulesData.rows.find(
				(s) => s.id === scheduleIdFromUrl,
			);
			if (schedule) {
				openPMScheduleOverview({
					scheduleId: schedule.id,
					onClose: () => {
						navigate('/pm-schedules');
					},
					onSuccess: () => {
						refreshSchedules({
							pageSize: null,
							status: statusFilter,
							...(assetFilter && { assetId: assetFilter }),
						});
					},
				});
			}
		}
	}, [scheduleIdFromUrl, schedulesData]);

	// Stats
	const total = schedulesData?.rows?.length || 0;
	const dueSoon =
		schedulesData?.rows?.filter((s) => {
			if (!s.nextDueDate) return false;
			const daysUntilDue = moment(s.nextDueDate).diff(moment(), 'days');
			return daysUntilDue <= 7 && daysUntilDue >= 0;
		}).length || 0;
	const overdue =
		schedulesData?.rows?.filter((s) => {
			if (!s.nextDueDate) return false;
			return moment(s.nextDueDate).isBefore(moment(), 'day');
		}).length || 0;
	const withAssets =
		schedulesData?.rows?.filter(
			(s) => s.directAssets?.length > 0 || s.contract?.assets?.length > 0,
		).length || 0;

	const isLoading = loadingStatus === 'pending';

	// Mobile card component
	const PMScheduleCard = ({ schedule }) => (
		<div
			className="bg-white border border-gray-200 rounded-lg p-4 cursor-pointer hover:border-gray-300 transition-colors"
			onClick={() => handleRowClick(schedule)}
		>
			<div className="flex items-start justify-between mb-2">
				<FrequencyBadge frequency={schedule.occurrences} />
				<StatusBadge active={schedule.active} />
			</div>
			<h3 className="text-sm font-medium text-gray-900 mb-2">
				{schedule.name || 'Untitled Schedule'}
			</h3>
			<div className="flex flex-wrap gap-2 mb-3">
				<span className="text-xs text-gray-500">
					{schedule.category?.name || 'Uncategorized'}
				</span>
				{schedule.contract && (
					<>
						<span className="text-xs text-gray-300">|</span>
						<span className="text-xs text-gray-500">
							{schedule.contract.name}
						</span>
					</>
				)}
			</div>
			{schedule.nextDueDate && (
				<div className="text-xs text-gray-400 mb-3">
					Next Due: {moment(schedule.nextDueDate).format('MMM D, YYYY')}
				</div>
			)}
			<div className="flex items-center justify-between pt-3 border-t border-gray-100">
				<div className="flex items-center gap-2">
					{(schedule.directAssets?.length > 0 ||
						schedule.contract?.assets?.length > 0) && (
						<span className="text-xs text-gray-500">
							{(schedule.directAssets?.length || 0) +
								(schedule.contract?.assets?.length || 0)}{' '}
							assets
						</span>
					)}
				</div>
				<span className="text-xs text-gray-400">
					Lead: {schedule.generateDaysBefore}d
				</span>
			</div>
		</div>
	);

	const columns = [
		{
			key: 'name',
			header: 'Name',
			render: (schedule) => (
				<TextStack
					primary={schedule.name}
					primaryFallback="Untitled Schedule"
					secondary={[
						schedule.category?.name || 'Uncategorized',
						schedule.contract?.name,
					]}
					capitalize
				/>
			),
		},
		{
			key: 'occurrences',
			header: 'Frequency',
			width: '120px',
			render: (schedule) => <FrequencyBadge frequency={schedule.occurrences} />,
		},
		{
			key: 'nextDueDate',
			header: 'Next Due',
			width: '140px',
			render: (schedule) => {
				if (!schedule.nextDueDate)
					return <span className="text-xs text-gray-400">-</span>;
				const isOverdue = moment(schedule.nextDueDate).isBefore(
					moment(),
					'day',
				);
				const isDueSoon =
					moment(schedule.nextDueDate).diff(moment(), 'days') <= 7 &&
					moment(schedule.nextDueDate).diff(moment(), 'days') >= 0;
				return (
					<span
						className={`text-xs ${
							isOverdue
								? 'text-red-600 font-medium'
								: isDueSoon
									? 'text-amber-600 font-medium'
									: 'text-gray-500'
						}`}
					>
						{moment(schedule.nextDueDate).format('MMM D, YYYY')}
					</span>
				);
			},
		},
		{
			key: 'generateDaysBefore',
			header: 'Lead Time',
			width: '100px',
			render: (schedule) => (
				<span className="text-xs text-gray-500">
					{schedule.generateDaysBefore} days
				</span>
			),
		},
		{
			key: 'assets',
			header: 'Assets',
			width: '80px',
			render: (schedule) => {
				const directCount = schedule.directAssets?.length || 0;
				const contractCount = schedule.contract?.assets?.length || 0;
				const total = directCount + contractCount;
				return (
					<span className="text-xs text-gray-500">
						{total > 0 ? total : '-'}
					</span>
				);
			},
		},
		{
			key: 'active',
			header: 'Status',
			width: '90px',
			render: (schedule) => <StatusBadge active={schedule.active} />,
		},
	];

	return (
		<MainContent maxWidth={'w-full'}>
			<PageHeading
				actions={[
					{
						name: 'Create PM Schedule',
						onClick: handleCreate,
					},
				]}
				divider={false}
			>
				PM Schedules
			</PageHeading>

			{/* Stats */}
			<div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
				<StatCard label="Total Schedules" value={total} />
				<StatCard
					label="Due Soon (7 days)"
					value={dueSoon}
					valueColor="text-amber-600"
				/>
				<StatCard label="Overdue" value={overdue} valueColor="text-red-600" />
				<StatCard
					label="With Assets"
					value={withAssets}
					valueColor="text-blue-600"
				/>
			</div>

			{/* View Toggle & Filters */}
			<div className="bg-white border border-gray-200 rounded-lg mb-6">
				<div className="flex flex-col lg:flex-row lg:items-center gap-4 p-4">
					{/* View Toggle */}
					<ViewToggle view={view} onViewChange={setView} />

					{/* Divider */}
					<div className="hidden lg:block w-px h-8 bg-gray-200" />

					{/* Search (only for table view) */}
					{view === 'table' && (
						<>
							<div className="flex-1 relative">
								<MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
								<input
									type="text"
									value={search}
									onChange={(e) => setSearch(e.target.value)}
									placeholder="Search PM schedules..."
									className="w-full pl-12 pr-4 py-3 text-sm bg-transparent border border-gray-200 lg:border-0 rounded-lg lg:rounded-none focus:outline-none focus:ring-0 placeholder:text-gray-400"
								/>
							</div>

							{/* Divider */}
							<div className="hidden lg:block w-px h-8 bg-gray-200" />

							{/* Filters */}
							<div className="flex items-center gap-3 flex-wrap">
								<AdjustmentsHorizontalIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
								<FilterDropdown
									label="Status"
									placeholder="Active"
									value={statusFilter}
									options={[
										{ value: 'active', label: 'Active' },
										{ value: 'inactive', label: 'Inactive' },
										{ value: 'all', label: 'All Status' },
									]}
									onChange={setStatusFilter}
								/>

								{categories.length > 0 && (
									<FilterDropdown
										label="Category"
										placeholder="All Categories"
										value={categoryFilter}
										options={[
											{ value: 'all', label: 'All Categories' },
											...categories.map((cat) => ({
												value: cat.id,
												label: cat.name,
											})),
										]}
										onChange={setCategoryFilter}
									/>
								)}
							</div>
						</>
					)}
				</div>
			</div>

			{/* Calendar View */}
			{view === 'calendar' && (
				<div className="bg-white border border-gray-200 rounded-lg p-6">
					{/* Bar Chart - Estimated PMs by Month */}
					<PMScheduleBarChart
						schedules={schedulesData?.rows || []}
						onScheduleClick={(schedule) => {
							navigate(`/pm-schedules/${schedule.id}`);
							openPMScheduleOverview({
								scheduleId: schedule.id,
								onClose: () => {
									navigate('/pm-schedules');
								},
								onSuccess: () => {
									refreshSchedules({
										pageSize: null,
										status: statusFilter,
										...(assetFilter && { assetId: assetFilter }),
									});
								},
							});
						}}
					/>
				</div>
			)}

			{/* Table View - Mobile/Tablet Cards */}
			{view === 'table' && (
				<>
					<div className="lg:hidden space-y-3">
						{isLoading ? (
							<div className="text-center py-8 text-gray-500">Loading...</div>
						) : processedSchedules.length === 0 ? (
							<div className="text-center py-8">
								<p className="text-gray-900 font-medium">
									No PM schedules found
								</p>
								<p className="text-gray-500 text-sm">
									Try adjusting your search or filter criteria
								</p>
							</div>
						) : (
							<>
								{processedSchedules
									.slice(0, mobileItemsToShow)
									.map((schedule) => (
										<PMScheduleCard key={schedule.id} schedule={schedule} />
									))}
								{/* Load more sentinel */}
								{mobileItemsToShow < processedSchedules.length && (
									<div
										ref={loadMoreRef}
										className="text-center py-4 text-gray-400 text-sm"
									>
										Loading more...
									</div>
								)}
							</>
						)}
					</div>

					{/* Table View - Desktop Table */}
					<div className="hidden lg:block">
						<DataTable
							data={processedSchedules}
							columns={columns}
							keyField="id"
							loading={isLoading}
							onRowClick={handleRowClick}
							defaultSort={{ key: 'nextDueDate', direction: 'asc' }}
							containerClassName="border border-gray-200 rounded-lg"
							emptyTitle="No PM schedules found"
							emptyDescription="Try adjusting your search or filter criteria"
							compact
							pagination
							pageSizeOptions={[10, 25, 50]}
						/>
					</div>
				</>
			)}
		</MainContent>
	);
};

export default PMSchedules;
